/**
 * The rows every model choice is made from, drawn once: the ticked
 * single-line row (the Default row and the family list), the two-line tier
 * rows under "Recommended", the "All models" disclosure that folds the
 * family list away, and a family group with its priced heading. The
 * composer's popover (ModelPicker) and the settings pages (ModelTierSelect)
 * build from these, so a tier looks the same wherever it is chosen (owner,
 * 2026-09-02: the account and product defaults "should only show 3 options
 * too just like chat model picker", "presented the same way").
 *
 * Roles are the caller's: a popover is a menu (menuitemradio rows, tier rows
 * plain menuitems the way the reference has them), an inline settings
 * control is a radiogroup (every row a radio, every row tabbable).
 */

import type { Effort } from '../../api/types';
import type { ReactNode, Ref } from 'react';

import {
  MODEL_TIERS,
  modelDisplayName,
  tierCostNote,
  type ModelGroup,
  type ModelTier,
} from './labels';

const MICRO = 'text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)]';

export function CheckGlyph() {
  return (
    <svg
      width={13}
      height={13}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4.5 12.5 9.5 17.5 19.5 6.5" />
    </svg>
  );
}

function rowClass(checked: boolean): string {
  return `flex w-full cursor-pointer rounded-card border-none px-2 text-left text-[length:var(--text-ui-size)] transition-colors duration-(--motion-hover) disabled:cursor-not-allowed disabled:opacity-50 ${
    checked
      ? 'bg-inset font-medium text-primary'
      : 'bg-transparent text-secondary hover:bg-inset hover:text-primary'
  }`;
}

/** One single-line pickable row: a tick, a label, and a quiet right-hand note. */
export function ModelRow({
  checked,
  label,
  meta,
  role,
  tabIndex,
  testId,
  onClick,
  innerRef,
  disabled = false,
}: {
  checked: boolean;
  label: string;
  /** the quiet right-hand line (the default row names what it resolves to) */
  meta: string | null;
  role: 'menuitemradio' | 'radio';
  tabIndex: number;
  testId: string;
  onClick: () => void;
  innerRef?: Ref<HTMLButtonElement>;
  disabled?: boolean;
}) {
  return (
    <button
      ref={innerRef}
      type="button"
      role={role}
      aria-checked={checked}
      data-testid={testId}
      tabIndex={tabIndex}
      disabled={disabled}
      onClick={onClick}
      className={`h-10 items-center gap-2 ${rowClass(checked)}`}
    >
      <span className="flex w-4 shrink-0 justify-center text-(--accent)">
        {checked ? <CheckGlyph /> : null}
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {meta !== null ? <span className="shrink-0 truncate text-tertiary">{meta}</span> : null}
    </button>
  );
}

/**
 * The recommended tiers (labels.MODEL_TIERS): the bench's picks, named by
 * what they buy, with a rough per-answer cost. Two lines, no truncation: the
 * name on top, why and the cost underneath (a one-line cut squeezed
 * "Balanced · Opus 5" against the price and clipped it).
 */
export function TierRows({
  model,
  effort,
  testId,
  role,
  rowTabIndex = -1,
  disabled = false,
  divider = true,
  onPick,
}: {
  /** the value on screen; the tier whose model this is reads as current */
  model: string | null;
  /** the stored effort beside it, when the setting has one: a tier is a
   * model AND an effort, so it reads as current only with both (omit to
   * match on the model alone) */
  effort?: Effort | null;
  testId: string;
  role: 'menuitem' | 'radio';
  rowTabIndex?: number;
  disabled?: boolean;
  /** a rule above the heading: only when a row sits above it (2026-09-03) */
  divider?: boolean;
  onPick: (tier: ModelTier) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Recommended"
      data-testid={`${testId}-tiers`}
      className={divider ? 'mt-1 border-t border-default pt-1' : ''}
    >
      <div aria-hidden className={`flex h-6 items-center px-2 font-medium text-tertiary ${MICRO}`}>
        Recommended
      </div>
      {MODEL_TIERS.map((tier) => {
        const current = tier.model === model && (effort === undefined || effort === tier.effort);
        return (
          <button
            key={tier.key}
            type="button"
            role={role}
            aria-current={role === 'menuitem' && current ? 'true' : undefined}
            aria-checked={role === 'radio' ? current : undefined}
            data-testid={`${testId}-tier-${tier.key}`}
            tabIndex={rowTabIndex}
            disabled={disabled}
            title={`${tier.label}: ${tier.why}`}
            onClick={() => onPick(tier)}
            className={`items-start py-1.5 ${rowClass(current)}`}
          >
            <span className="flex h-5 w-4 shrink-0 items-center justify-center text-(--accent)">
              {current ? <CheckGlyph /> : null}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate">
                {tier.label}
                <span className="text-tertiary"> · {modelDisplayName(tier.model)}</span>
              </span>
              <span className={`font-normal text-tertiary ${MICRO}`}>
                {tier.why} · {tierCostNote(tier)}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** The disclosure the family list sits behind: "+" folded, a minus unfolded. */
export function AllModelsToggle({
  open,
  onToggle,
  testId,
}: {
  open: boolean;
  onToggle: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      data-testid={`${testId}-all-models`}
      aria-expanded={open}
      onClick={onToggle}
      className={`mt-1 flex h-7 w-full cursor-pointer items-center gap-2 rounded-card border-t border-default bg-transparent px-2 text-left font-medium text-tertiary transition-colors duration-(--motion-hover) hover:text-primary ${MICRO}`}
    >
      <span className="min-w-0 flex-1">All models</span>
      <span aria-hidden>{open ? '−' : '+'}</span>
    </button>
  );
}

/** One family (Fable, Opus, Sonnet) under its heading, the price said once there. */
export function FamilyGroup({ group, children }: { group: ModelGroup; children: ReactNode }) {
  return (
    <div
      role="group"
      aria-label={group.note === null ? group.label : `${group.label}, ${group.note}`}
      className="mt-1 border-t border-default pt-1"
    >
      <div
        aria-hidden
        className={`flex h-6 items-center gap-2 px-2 font-medium text-tertiary ${MICRO}`}
      >
        <span className="min-w-0 truncate">{group.label}</span>
        {group.note !== null ? (
          <span className="ml-auto shrink-0 font-normal">{group.note}</span>
        ) : null}
      </div>
      {children}
    </div>
  );
}
